import { useCallback } from 'react';
import {
  useDebugLogContextOptional,
  type DebugLogCategory,
  type DebugLogLevel,
  type DebugContextInfo
} from './DebugLogContext';

type LogParams = {
  category: DebugLogCategory;
  level?: DebugLogLevel;
  title: string;
  summary?: string;
  details?: unknown;
};

type UseDebugLogResult = {
  log: (params: LogParams) => void;
  logApi: (
    method: string,
    url: string,
    params?: { status?: number; duration?: number; response?: unknown; error?: unknown }
  ) => void;
  logPusher: (event: string, payload?: unknown) => void;
  logError: (title: string, error: unknown) => void;
  logMessage: (direction: 'sent' | 'received', text: string, details?: unknown) => void;
  logState: (title: string, details?: unknown) => void;
  setContextInfo: (info: Partial<Omit<DebugContextInfo, 'tripId'>>) => void;
  isEnabled: boolean;
};

export const useDebugLog = (): UseDebugLogResult => {
  const context = useDebugLogContextOptional();

  const log = useCallback(
    (params: LogParams) => {
      if (!context?.isEnabled) return;
      context.addLog({
        category: params.category,
        level: params.level || 'info',
        title: params.title,
        summary: params.summary,
        details: params.details
      });
    },
    [context]
  );

  const logApi = useCallback(
    (
      method: string,
      url: string,
      params?: { status?: number; duration?: number; response?: unknown; error?: unknown }
    ) => {
      if (!context?.isEnabled) return;
      const isError = params?.error || (params?.status && params.status >= 400);
      const summary = params?.error
        ? `Error: ${params.error instanceof Error ? params.error.message : String(params.error)}`
        : params?.status
        ? `${params.status} (${params.duration ?? '?'}ms)`
        : undefined;

      context.addLog({
        category: 'api',
        level: isError ? 'error' : 'info',
        title: `${method} ${url}`,
        summary,
        details: params?.response || params?.error
      });
    },
    [context]
  );

  const logPusher = useCallback(
    (event: string, payload?: unknown) => {
      if (!context?.isEnabled) return;
      context.addLog({
        category: 'pusher',
        level: 'info',
        title: `Pusher: ${event}`,
        details: payload
      });
    },
    [context]
  );

  const logError = useCallback(
    (title: string, error: unknown) => {
      if (!context?.isEnabled) return;
      context.addLog({
        category: 'error',
        level: 'error',
        title,
        summary: error instanceof Error ? error.message : String(error),
        details: error instanceof Error ? { message: error.message, stack: error.stack } : error
      });
    },
    [context]
  );

  const logMessage = useCallback(
    (direction: 'sent' | 'received', text: string, details?: unknown) => {
      if (!context?.isEnabled) return;
      context.addLog({
        category: 'message',
        level: 'info',
        title: direction === 'sent' ? 'Message Sent' : 'Message Received',
        summary: text.length > 50 ? `${text.substring(0, 50)}...` : text,
        details: details || { text }
      });
    },
    [context]
  );

  const logState = useCallback(
    (title: string, details?: unknown) => {
      if (!context?.isEnabled) return;
      context.addLog({
        category: 'state',
        level: 'info',
        title,
        details
      });
    },
    [context]
  );

  const setContextInfo = useCallback(
    (info: Partial<Omit<DebugContextInfo, 'tripId'>>) => {
      if (!context?.isEnabled) return;
      context.setContextInfo(info);
    },
    [context]
  );

  return {
    log,
    logApi,
    logPusher,
    logError,
    logMessage,
    logState,
    setContextInfo,
    isEnabled: context?.isEnabled ?? false
  };
};
